import { motion } from 'framer-motion';

const PINK_AVATAR = "https://customer-assets.emergentagent.com/job_sophia-voice/artifacts/ugjvqetx_369ailove_An_elderly_woman_inspired_by_Iris_Apfel_in_a_super__b15f44a3-bf81-4c38-83d7-69cc33c45666_3.png";
const TEAL_AVATAR = "https://customer-assets.emergentagent.com/job_sophia-voice/artifacts/gt8nvcuk_369ailove_An_elderly_woman_inspired_by_Iris_Apfel_in_a_super__0d282bb3-7e4c-41bf-b88a-4e9c40f77439_3.png";

export function SophiaAvatar({ variant = "pink", size = "lg", animate = true, isSpeaking = false, className = "" }) {
  const src = variant === "teal" ? TEAL_AVATAR : PINK_AVATAR;

  const sizeClasses = {
    sm: "w-14 h-14",
    md: "w-24 h-24",
    lg: "w-36 h-36",
    xl: "w-48 h-48",
    hero: "w-52 h-52 md:w-64 md:h-64",
  };

  const containerSizes = {
    sm: "w-14 h-14",
    md: "w-24 h-24",
    lg: "w-36 h-36",
    xl: "w-48 h-48",
    hero: "w-52 h-52 md:w-64 md:h-64",
  };

  return (
    <motion.div
      className={`relative flex items-center justify-center ${containerSizes[size]} ${className}`}
      initial={animate ? { scale: 0.8, opacity: 0 } : false}
      animate={animate ? { scale: 1, opacity: 1 } : false}
      transition={{ type: "spring", stiffness: 200, damping: 20 }}
    >
      {/* Glow ring */}
      <div className={`absolute inset-[-8px] rounded-full ${isSpeaking ? 'bg-sophia-pink-500/30' : 'bg-sophia-pink-500/15'} blur-xl transition-all duration-500`} />
      
      {/* Speaking pulse rings */}
      {isSpeaking && (
        <>
          <motion.div
            className="absolute inset-[-12px] rounded-full border-2 border-sophia-pink-500/40"
            animate={{ scale: [1, 1.15, 1], opacity: [0.5, 0, 0.5] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute inset-[-20px] rounded-full border border-sophia-pink-300/20"
            animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0, 0.3] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
          />
        </>
      )}

      {/* Avatar image */}
      <motion.img
        src={src}
        alt="Sophia Einstein"
        data-testid="sophia-avatar-img"
        className={`${sizeClasses[size]} rounded-full object-cover object-top border-2 ${isSpeaking ? 'border-sophia-pink-500/60' : 'border-sophia-pink-500/30'} shadow-2xl relative z-10 transition-all duration-300`}
        animate={
          isSpeaking
            ? { y: [0, -3, 0], scale: [1, 1.02, 1] }
            : animate
            ? { y: [0, -6, 0] }
            : false
        }
        transition={
          isSpeaking
            ? { duration: 0.8, repeat: Infinity, ease: "easeInOut" }
            : animate
            ? { duration: 4, repeat: Infinity, ease: "easeInOut" }
            : undefined
        }
      />
      
      {/* Speaking mouth indicator */}
      {isSpeaking && (
        <motion.div
          className="absolute bottom-[18%] left-1/2 -translate-x-1/2 z-20 flex gap-0.5"
          animate={{ opacity: [0.6, 1, 0.6] }}
          transition={{ duration: 0.4, repeat: Infinity }}
        >
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              className="w-1 bg-sophia-pink-500 rounded-full"
              animate={{ height: [3, 8 + i * 2, 3] }}
              transition={{ duration: 0.3 + i * 0.1, repeat: Infinity, delay: i * 0.1 }}
            />
          ))}
        </motion.div>
      )}
      
      {/* Sparkle dots */}
      {animate && !isSpeaking && (
        <>
          <motion.div
            className="absolute top-1 right-1 w-2 h-2 bg-sophia-pink-300 rounded-full z-20"
            animate={{ opacity: [0, 1, 0], scale: [0.5, 1, 0.5] }}
            transition={{ duration: 2, repeat: Infinity, delay: 0 }}
          />
          <motion.div
            className="absolute bottom-3 left-0 w-1.5 h-1.5 bg-mystic-teal-300 rounded-full z-20"
            animate={{ opacity: [0, 1, 0], scale: [0.5, 1, 0.5] }}
            transition={{ duration: 2, repeat: Infinity, delay: 0.7 }}
          />
        </>
      )}
    </motion.div>
  );
}
